import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Pencil, Trash } from "lucide-react";

import { formatMilesDecimal } from "@/services/Utils.jsx";

import CustomDropdown from "@/components/desktop/list/dropdown/CustomDropdown";
import CustomBadge from "@/components/desktop/badge/CustomBadge";

const COLUMN_WIDTHS = {
	index: "4%",
	name: "16%",
	estates: "20%",
	region: "10%",
	locality: "10%",
	hectares: "12%",
	timeOpen: "9%",
	timeClose: "9%",
	action: "10%",
};

export function useEstatesParentsColumns({ tableState, estates, regions, canEdit, onDelete }) {
	const navigate = useNavigate();

	return useMemo(() => {
		return [
			{
				Header: "#",
				accessor: "",
				disableSortBy: true,
				width: COLUMN_WIDTHS.index,
				Cell: ({ row }) => <span>{(tableState.page - 1) * tableState.per_page + row.index + 1}</span>,
			},
			{
				Header: "Nombre",
				accessor: "name",
				width: COLUMN_WIDTHS.name,
			},
			{
				Header: "Bodegas",
				accessor: "estates",
				width: COLUMN_WIDTHS.estates,
				disableSortBy: true,
				Cell: ({ value }) => {
					if (!value?.length) return null;
					const estateIds = value.map((id) => Number(id));
					const estateItems = estates.filter((e) => estateIds.includes(Number(e.value)));
					return (
						<div style={{ display: "flex", flexWrap: "wrap", gap: "4px" }}>
							{estateItems.map((estate) => (
								<CustomBadge key={estate.value} variant="neutral">
									{estate.label}
								</CustomBadge>
							))}
						</div>
					);
				},
			},
			{
				Header: "Región",
				accessor: "region",
				width: COLUMN_WIDTHS.region,
				disableSortBy: true,
				Cell: ({ value }) => {
					const regionId = value ?? null;
					if (!regionId || !regions.length) return "";
					return regions.find((r) => Number(r.value) === Number(regionId))?.label ?? "";
				},
			},
			{
				Header: "Comuna",
				id: "locality",
				accessor: "locality_id",
				width: COLUMN_WIDTHS.locality,
				disableSortBy: true,
				Cell: ({ row }) => {
					const data = row?.original;
					if (!data) return "";
					const regionId = data.region ?? data.region_id ?? null;
					const localityId = data.locality ?? data.locality_id ?? null;
					if (!regionId || !localityId || !regions.length) return "";
					const region = regions.find((r) => Number(r.value) === Number(regionId));
					return region?.localities?.find((l) => Number(l.value) === Number(localityId))?.label ?? "";
				},
			},
			{
				Header: "Hectáreas",
				accessor: "hectares",
				width: COLUMN_WIDTHS.hectares,
				disableSortBy: true,
				Cell: ({ value }) => (value ? `${formatMilesDecimal(value)} ha` : ""),
			},
			{ Header: "Hora Apertura", accessor: "time_open", width: COLUMN_WIDTHS.timeOpen, disableSortBy: true },
			{ Header: "Hora Cierre", accessor: "time_close", width: COLUMN_WIDTHS.timeClose, disableSortBy: true },
			{
				Header: "Acción",
				accessor: "slug",
				disableSortBy: true,
				width: COLUMN_WIDTHS.action,
				Cell: ({ value, row }) => {
					const items = [];

					if (canEdit) {
						items.push(
							{
								label: "Editar",
								action: () => navigate("/configuracion/grupos_campos/editar/" + value),
								icon: Pencil,
							},
							{
								label: "Eliminar",
								action: () => onDelete(row.original.id),
								icon: Trash,
								danger: true,
							},
						);
					}

					if (!items.length) return null;

					return <CustomDropdown title="Acciones" items={items} align="right" />;
				},
			},
		];
	}, [tableState.page, tableState.per_page, estates, regions, canEdit, navigate, onDelete]);
}

export default useEstatesParentsColumns;
